import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, Zoom } from 'swiper/modules';
import { X } from 'lucide-react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/zoom';

const ImageSliderModal = ({
  isOpen,
  onClose,
  images,
  initialSlideIndex = 0,
}) => {
  const [swiper, setSwiper] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (swiper && isOpen) {
      swiper.slideTo(initialSlideIndex, 0);
    }
  }, [swiper, isOpen, initialSlideIndex]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <SwiperContainer>
          <Swiper
            modules={[Navigation, Keyboard, Zoom]}
            spaceBetween={0}
            slidesPerView={1}
            navigation
            keyboard={{ enabled: true }}
            zoom={{ maxRatio: 3 }}
            loop={true}
            onSwiper={setSwiper}
            initialSlide={initialSlideIndex}
            touchRatio={1.5}
            resistanceRatio={0.85}
            threshold={5}
          >
            {images.map((image, index) => (
              <SwiperSlide key={image.id || index}>
                <div className="swiper-zoom-container">
                  <Image
                    src={image.image}
                    alt={`갤러리 이미지 ${image.id || index + 1}`}
                    loading="lazy"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperContainer>
        <CloseButton onClick={onClose}>
          <X size={24} />
        </CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  color: var(--background);
  z-index: 1010;
`;

const SwiperContainer = styled.div`
  width: 100%;
  height: 100%;

  .swiper {
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: var(--background);
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    padding: 1rem;

    &::after {
      font-size: 2rem;
      box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.04);
    }
  }

  .swiper-button-prev {
    left: 0;
  }

  .swiper-button-next {
    right: 0;
  }

  .swiper-zoom-container > img,
  .swiper-zoom-container > svg,
  .swiper-zoom-container > canvas {
    max-width: 75%;
    max-height: auto;
    object-fit: cover;
  }
`;

const Image = styled.img`
  cursor: zoom-in;
`;

export default ImageSliderModal;
